import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  VEGA_HELD_BAND_FR,
  PMS_AGE_BANDS,
  PMS_AGE_BAND_LABELS_FR,
  PMS_ALLERGENS,
  PMS_BOARD_LABELS,
  PMS_BOARD_TITLES_FR,
  PMS_MEALS,
  PMS_MEAL_LABELS_FR,
  PMS_STATUS_LABELS_FR,
  pmsEditRule,
  pmsMealIncluded,
  pmsMealServiceDate,
  pmsMealTaken,
  pmsNightCount,
  pmsNightVector,
  pmsPersonsShort,
  pmsPreviewRoom,
} from '@lemontaigu/pms-core';
import type {
  AgeBand,
  Allergen,
  GuestService,
  GuestSummary,
  JournalLine,
  MealPrestation,
  PmsBoard,
  Product,
  RatePlan,
  Room,
  RoomPreview,
  RoomType,
} from '@lemontaigu/pms-core';
import { fmtAmount, fmtDate, fmtEuros, parseEurosToCents } from './logic.js';
import { GuestPicker } from './GuestPicker.js';
import { ALLERGEN_LABELS_FR } from './allergens.js';
import {
  addPerson,
  addRoom,
  addRoomsProduct,
  formIsComplete,
  formIsDirty,
  groupSpan,
  patchPerson,
  patchRoom,
  personLabel,
  removePerson,
  removeRoom,
  roomBoard,
  roomCounts,
  setPersonNeeds,
  setRoomProductQty,
  setRoomsDates,
  setRoomsMeal,
  setRoomsPrice,
  stayNights,
  togglePersonAllergen,
  togglePersonMeal,
  type FormState,
  type PersonHealth,
  type RoomDraft,
} from './reservationDraft.js';
import { billedLines, type BilledLine } from './billedLines.js';
import { PAYMENT_STATE_LABEL_FR, amountText, paymentSummary, paymentTitle, type AdvanceRead } from './advanceView.js';
import { planGapNote, planGapText, planGapTitle, type PlanGapRead } from './planGapView.js';
import { roomNightsMark } from './roomLineView.js';
import { SoldTermsNote } from './SoldTerms.js';

// The booking form — « Modifier la réservation » — as both « Réservations »
// and « Plan d'occupation » mount it.
//
// It owns its DRAFT and hands the finished FormState back; the caller owns the
// services, the default rate plan and what saving means.
//
// ── The group design (PMS-GROUP-EDIT-BRIEF §2, mockup v4) ───────────────────
//
// One card per logement was right for « virtually every booking is a single
// room » and wrong for a 25-room group: 25 screens to read, no way to change
// all rooms at once, a wrong date on room 17 found by scrolling. So:
//
//   · a BAND says the group at a glance — client, total, summary chips;
//   · two ACTION ROWS edit many rooms at once (« Pour tous », « Sélection »);
//     they edit the draft only, and saving sends ONE command (§4);
//   · a ROOMING LIST shows every logement as one row (Q11: all of them —
//     hiding rows hides wrong dates); a click opens the row's DETAIL: its
//     named persons, their meals night by night, its per-night prices.
//
// A single room is the same component with nothing to select (Q12): no
// selection column, no « logements » chip, and its one row open by default —
// the detail IS the form.
//
// Colour carries meaning or is absent (UI-BRANDING §2): a type de séjour has
// its colour (DP violet · Pdj teal · Ch grey), a meal cell is in that colour
// when the rate includes it, green when it is billed to everyone present,
// amber when someone opted out on some night.

export interface ReservationFormProps {
  initial: FormState;
  roomTypes: RoomType[];
  /** The établissement's rate plans — each room's type de séjour is its own
   *  plan's board; the FIRST is the default for a room being created. */
  ratePlans: RatePlan[];
  /** The cardex, searched — never loaded whole (PMS-CARDEX-BRIEF §6.1 S6). */
  guests: Pick<GuestService, 'search' | 'sheet'>;
  /** Only to NAME a prefilled unit — the form never picks one. */
  rooms: Room[];
  /** The établissement's catalogue: agreed extras, the meal products, the
   *  taxe-de-séjour tariff. Empty is a legitimate state. */
  products: Product[];
  saving: boolean;
  error: string;
  /** Saves through the booking's établissement — the caller writes through
   *  THAT établissement's services (U3, UD2). Null only when the lists name a
   *  single établissement and nothing chose another. */
  onSave: (form: FormState, propertyCode: string | null) => void;
  onCancel: () => void;
  /** U3: the établissement this booking belongs to when something already
   *  names it — an existing booking's own, or the unit a rack cell was
   *  clicked on. Omitted: taken from the categories, or asked. */
  propertyCode?: string | null;
  /** U3: how to name each établissement the lists may span. */
  etablissementLabels?: Readonly<Record<string, string>>;
  /** The desk holds `pms.needs.read` (group edit step 5): each person shows
   *  their needs and allergens, which are health data. The CALLER decides,
   *  because it is also the one that loads them; without it they are neither
   *  shown nor sent, and the database keeps what it holds. */
  canSeeNeeds?: boolean;
  /** What the booking has paid (« Arrhes », other payments, its status), read by
   *  the caller with the rest of what the form opens with: null while reading, a
   *  message when the read failed. Omitted = the caller has none to show (Réservations). Shown
   *  for an existing booking only — a new one has paid nothing yet. */
  advance?: AdvanceRead;
  /** What the booking was billed on the WHOLE BOOKING — breakfast, levy, bar —
   *  at the price it was billed (`billedLines`). A reading, never edited and never
   *  saved. Omitted or empty: nothing is listed. */
  billed?: readonly BilledLine[];
  /** The booking's history, newest first (`journalLines`, O5): what was done,
   *  when, and by whom — « par Marie D. ». Omitted or empty: no « Journal ». */
  journal?: readonly JournalLine[];
  /** Where Vega's charges and its rooming list disagree for this booking
   *  (« Vega facture 5 chambres pour 9 logements »), read by the caller: null while
   *  reading or when there is nothing to say, a message when the read failed.
   *  Omitted = the caller has none to show. */
  planGap?: PlanGapRead;
  /** Tells the caller whether the draft differs from what the form opened with,
   *  so IT can ask before closing over typed work. Called on every change. */
  onDirtyChange?: (dirty: boolean) => void;
  /** OB2 (PMS-OWNED-BOOKINGS-BRIEF Q1): the booking is « tenue par Vega ». The
   *  form is then a reading — every field disabled, every room open, no
   *  « Enregistrer » — because the seed would put back whatever was saved, and
   *  the database refuses the save anyway. */
  vegaHeld?: boolean;
}

/** « Arrhes 90,66 € », the other payments when there are any, and the booking's
 *  payment status — all against the form's own total, so a price edited here moves
 *  the status before anything is saved. */
function PaymentChips({ advance, totalCents }: { advance: AdvanceRead; totalCents: number }) {
  const title = paymentTitle(advance, totalCents) ?? undefined;
  const s = advance === null || typeof advance === 'string' ? null : paymentSummary(advance, totalCents);
  return (
    <span className="pms-ge-pay">
      <span className="pms-ge-chip" title={title}>
        Arrhes {amountText(advance, (x) => x.arrhesCents, totalCents)}
      </span>
      {s !== null && s.otherCents !== 0 && (
        <span className="pms-ge-chip" title={title}>
          Autres règlements {amountText(advance, (x) => x.otherCents, totalCents)}
        </span>
      )}
      {s?.state != null && (
        <span className={`pms-ge-chip ${s.state === 'paid' ? 'is-ok' : 'is-warn'}`} title={title}>
          {PAYMENT_STATE_LABEL_FR[s.state]}
          {s.state === 'partial' && ` · reste ${fmtEuros(s.dueCents)}`}
        </span>
      )}
    </span>
  );
}

/** « Prestations facturées » — the booking's own lines at their billed price, with the
 *  catalogue price beside one that differs (a breakfast at 9,00 where the catalogue says 11,00). */
function BilledList({ lines }: { lines: readonly BilledLine[] }) {
  const total = lines.reduce((s, l) => s + l.amountCents, 0);
  return (
    <section className="pms-ge-billed" aria-label="Prestations facturées">
      <h4>Prestations facturées sur la réservation</h4>
      <table className="pms-table">
        <thead>
          <tr>
            <th>Produit</th>
            <th className="pms-ge-num">Qté</th>
            <th className="pms-ge-num">Prix</th>
            <th className="pms-ge-num">Montant</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={`${l.kind}|${l.label}|${l.unitCents}`}>
              <td>{l.label}</td>
              <td className="pms-ge-num">{l.quantity}</td>
              <td className="pms-ge-num">
                {fmtEuros(l.unitCents)}
                {l.catalogueCents !== null && (
                  <span className="pms-sub" title="Le prix du catalogue, différent de celui facturé">
                    {' '}
                    (catalogue {fmtEuros(l.catalogueCents)})
                  </span>
                )}
              </td>
              <td className="pms-ge-num">{fmtEuros(l.amountCents)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3}>Total facturé hors logements</td>
            <td className="pms-ge-num">{fmtEuros(total)}</td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
}

/** « 30/12 ». */
const dm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

/** One établissement's part of a list (U3). With one établissement present the
 *  list is returned untouched — the single-building form is unchanged. With
 *  several and none chosen yet: nothing — a booking's categories wait for its
 *  établissement. */
function narrowTo<T extends { propertyCode: string }>(list: T[], etab: string | null, several: boolean): T[] {
  if (!several) return list;
  return etab === null ? [] : list.filter((x) => x.propertyCode === etab);
}

/** An agreed extra the desk may CHOOSE (§1, in-stay charges Q2): not the levy,
 *  not auto-applied, not a meal (those are switches), and not a POSTING
 *  vehicle — the proof catalogue's one product per Vega CA category
 *  (`CA100`…`CA990`, 20261013100100), posted with an amount and never chosen
 *  at booking, pass-throughs included. A 0 € extra such as a cot stays: price
 *  is not what makes a posting vehicle. */
const POSTING_VEHICLE = /^CA\d+$/;
const isAgreedExtra = (p: Product) =>
  p.active && !p.isTax && !p.autoApply && !p.boardPrestation && !p.isPassthrough && !POSTING_VEHICLE.test(p.code);

type Scope = 'all' | 'selection' | number;
type Panel = { scope: Scope; kind: 'dates' | 'price' | 'product' } | null;

const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

/** « Journal » — who did what to the booking (ADR-0026, O5). Read-only. */
function Journal({ lines }: { lines: readonly JournalLine[] }) {
  return (
    <details className="pms-ge-details pms-journal">
      <summary>Journal ({lines.length})</summary>
      <ul>
        {lines.map((l) => (
          <li key={l.id}>
            <span className="pms-journal-when">{fmtWhen(l.at)}</span> · {l.what}
            {l.who && <span className="pms-journal-who"> · {l.who}</span>}
          </li>
        ))}
      </ul>
    </details>
  );
}

export function ReservationForm({
  initial,
  roomTypes: allRoomTypes,
  ratePlans: allRatePlans,
  guests,
  rooms: allRooms,
  products: allProducts,
  saving,
  error,
  onSave,
  onCancel,
  canSeeNeeds = false,
  advance,
  billed = [],
  journal = [],
  planGap,
  onDirtyChange,
  propertyCode = null,
  etablissementLabels = {},
  vegaHeld = false,
}: ReservationFormProps) {
  const [form, setForm] = useState<FormState>(initial);

  // ── U3: the booking's établissement (PMS-UNIFIED-DESK-BRIEF §4, UD2) ──────
  // The lists may span several établissements (the unified rack). A booking
  // belongs to ONE (rule 2): named by what opened the form, else by the
  // categories it already has, else asked — with no default. Every list is
  // narrowed to it here, so the rest of the form reads only that building's
  // categories, plans, units and extras. With one établissement nothing
  // changes: it is that one, and nothing is asked.
  const present = useMemo(() => [...new Set(allRoomTypes.map((t) => t.propertyCode))], [allRoomTypes]);
  const several = present.length > 1;
  const anchorOf = (f: FormState): string | null =>
    propertyCode ??
    f.rooms
      .map((r) => allRoomTypes.find((t) => String(t.id) === r.roomTypeId)?.propertyCode)
      .find((p): p is string => p !== undefined) ??
    (present.length === 1 ? present[0]! : null);
  const [etab, setEtab] = useState<string | null>(() => anchorOf(initial));
  const roomTypes = useMemo(() => narrowTo(allRoomTypes, etab, several), [allRoomTypes, etab, several]);
  const ratePlans = useMemo(() => narrowTo(allRatePlans, etab, several), [allRatePlans, etab, several]);
  const rooms = useMemo(() => narrowTo(allRooms, etab, several), [allRooms, etab, several]);
  const products = useMemo(() => narrowTo(allProducts, etab, several), [allProducts, etab, several]);
  const labelOf = (code: string) => etablissementLabels[code] ?? code;
  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));
  const setRoom = (index: number, patch: Partial<RoomDraft>) => setForm((f) => patchRoom(f, index, patch));
  const mapRoom = (index: number, fn: (r: RoomDraft) => RoomDraft) =>
    setForm((f) => ({ ...f, rooms: f.rooms.map((r, i) => (i === index ? fn(r) : r)) }));

  // The caller may finish loading persons and charge lines after the form has
  // opened; take its newer draft as long as the desk has not typed anything.
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!touched) {
      setForm(initial);
      setEtab(anchorOf(initial));
    }
    // anchorOf reads props only; `initial` is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, touched]);
  const edit = <A extends unknown[]>(fn: (...a: A) => void) => (...a: A) => {
    setTouched(true);
    fn(...a);
  };

  const dirty = useMemo(() => formIsDirty(initial, form), [initial, form]);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const multi = form.rooms.length > 1;
  // A Vega-held booking opens every room: its toggles are disabled with the rest.
  const [open, setOpen] = useState<Set<number>>(
    () => new Set(vegaHeld ? initial.rooms.map((_, i) => i) : initial.rooms.length === 1 ? [0] : []),
  );
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [panel, setPanel] = useState<Panel>(null);

  const extras = products.filter(isAgreedExtra);
  const automatic = products.filter((p) => !p.isTax && p.autoApply);
  const hasTax = products.some((p) => p.isTax && p.active);

  // Previewed PER ROOM and summed — which is also how the database writes it
  // (one derivation per logement).
  const boards = form.rooms.map((r) => roomBoard(r, ratePlans));
  const previews: RoomPreview[] = form.rooms.map((room, i) =>
    pmsPreviewRoom({
      board: boards[i]!,
      roomMeals: room.meals ?? [],
      nights: pmsNightVector(room.checkin, room.checkout, room.nights, parseEurosToCents(room.priceEuros) ?? 0),
      checkin: room.checkin,
      checkout: room.checkout,
      persons: room.persons.map((p) => ({ ...p })),
      catalogue: products,
      requested: room.products,
    }),
  );
  const total = previews.reduce((s, p) => s + p.totalCents, 0);
  const roomCents = previews.reduce((s, p) => s + p.roomCents, 0);
  const roomNights = form.rooms.reduce((n, r) => n + Math.max(pmsNightCount(r.checkin, r.checkout), 0), 0);
  const taxNights = previews.reduce((s, p) => s + p.taxableNights, 0);
  const taxCents = previews.reduce((s, p) => s + p.taxCents, 0);
  const span = groupSpan(form.rooms);
  const spanNights = Math.max(pmsNightCount(span.checkin, span.checkout), 0);
  const personCount = form.rooms.reduce((n, r) => n + r.persons.length, 0);
  const mix = (['hb', 'bb', 'none'] as PmsBoard[])
    .map((b) => ({ b, n: boards.filter((x) => x === b).length }))
    .filter((m) => m.n > 0);

  // ── The client (S3: a Vega-held identity is read-only) ────────────────────
  const [held, setHeld] = useState<boolean | null>(initial.guestId ? null : false);
  useEffect(() => {
    if (!initial.guestId) return;
    let cancelled = false;
    void guests.sheet(initial.guestId).then((r) => {
      if (!cancelled) setHeld(r.ok ? r.value.vegaHeld : false);
    });
    return () => {
      cancelled = true;
    };
  }, [guests, initial.guestId]);
  const pickGuest = edit((g: GuestSummary) => {
    setHeld(g.vegaHeld);
    set({
      guestId: g.id,
      lastName: g.lastName,
      firstName: g.firstName,
      email: g.email ?? '',
      // Exactly what the client holds: for a held client the database reads
      // any difference as a change and refuses.
      phone: g.phone ?? '',
      lang: g.lang,
    });
  });
  const newGuest = edit(() => {
    setHeld(false);
    set({ guestId: '', lastName: '', firstName: '', email: '', phone: '', lang: 'fr' });
  });
  const identityLocked = form.guestId !== '' && held !== false;

  // ── Bulk targets ──────────────────────────────────────────────────────────
  const targets = (scope: Scope): number[] =>
    scope === 'all' ? form.rooms.map((_, i) => i) : scope === 'selection' ? [...selected].sort() : [scope];

  /** ON when every room has the meal, by its rate or its switch. */
  const allHave = (meal: MealPrestation) =>
    form.rooms.every((r, i) => pmsMealIncluded(boards[i]!, meal) || (r.meals ?? []).includes(meal));
  const allInclude = (meal: MealPrestation) => boards.every((b) => pmsMealIncluded(b, meal));

  const toggleOpen = (i: number) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(i)) n.delete(i);
      else n.add(i);
      return n;
    });
  const toggleSelected = (i: number) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(i)) n.delete(i);
      else n.add(i);
      return n;
    });

  const removeRoomAt = edit((i: number) => {
    setForm((f) => removeRoom(f, i));
    setOpen(new Set());
    setSelected(new Set());
  });

  return (
    <section className="pms-form pms-ge">
      <h3>{vegaHeld ? 'Réservation tenue par Vega' : form.id ? 'Modifier la réservation' : 'Nouvelle réservation'}</h3>
      {vegaHeld && (
        <p className="pms-vega-band" role="note">
          {VEGA_HELD_BAND_FR}
        </p>
      )}

      {several &&
        (form.id !== null || propertyCode !== null ? (
          // Rule 2: a booking's établissement never changes once it exists —
          // and a cell already chose it.
          <p className="pms-ge-etab">
            Établissement : <strong>{etab === null ? '—' : labelOf(etab)}</strong>
          </p>
        ) : (
          <label className="pms-field pms-ge-etab">
            <span>Établissement</span>
            <select
              value={etab ?? ''}
              onChange={(e) => {
                setTouched(true);
                setEtab(e.target.value);
                // The rooms' categories, plans, units and extras were the other
                // building's: none of them can follow.
                setForm((f) => ({
                  ...f,
                  rooms: f.rooms.map((r) => ({ ...r, roomTypeId: '', ratePlanId: '', roomId: '', products: [] })),
                }));
              }}
            >
              <option value="" disabled>
                — choisir —
              </option>
              {present.map((code) => (
                <option key={code} value={code}>
                  {labelOf(code)}
                </option>
              ))}
            </select>
          </label>
        ))}

      {/* A Vega-held booking is a reading (Q1): one disabled fieldset rather than a
          flag threaded through every input, so a field added later is covered too. */}
      <fieldset className="pms-ge-fieldset" disabled={vegaHeld}>
      {/* ── The band: the group at a glance ─────────────────────────────── */}
      <div className="pms-ge-band">
        <div className="pms-ge-client">
          {form.guestId ? (
            <span className="pms-ge-name">
              <strong>{form.lastName.toUpperCase()}</strong> {form.firstName}
              {held && (
                <span className="pms-tag" title="Nom et coordonnées tenus à jour depuis Vega jusqu’à la mise en service">
                  Vega
                </span>
              )}
              <button type="button" className="pms-link" onClick={newGuest}>
                Changer de client
              </button>
            </span>
          ) : (
            <GuestPicker guests={guests} onPick={pickGuest} />
          )}
          <span className="pms-ge-meta">
            {form.lang.toUpperCase()}
            {form.channel !== '' && ` · ${form.channel}`}
            {form.groupName !== '' && (
              <>
                {' · '}
                <strong>{form.groupName}</strong>
              </>
            )}
          </span>
        </div>
        <div className="pms-ge-total" aria-label="Total">
          {roomNights > 0 ? fmtEuros(total) : '—'}
        </div>
      </div>

      <div className="pms-ge-chips" aria-label="Résumé">
        {spanNights > 0 && (
          <span className="pms-ge-chip">
            {dm(span.checkin)} → {dm(span.checkout)} · {spanNights} nuit{spanNights > 1 ? 's' : ''}
          </span>
        )}
        {multi && <span className="pms-ge-chip">{form.rooms.length} logements</span>}
        <span className="pms-ge-chip">{personCount} pers.</span>
        {roomNights > 0 && (
          <span className="pms-ge-chip">{fmtAmount(Math.round(roomCents / roomNights))} / nuit moy.</span>
        )}
        {mix.length > 0 && (
          <span className="pms-ge-chip">
            {mix.map((m, k) => (
              <Fragment key={m.b}>
                {k > 0 && ' · '}
                <span className={`pms-board-text is-${m.b}`}>
                  {m.n} {PMS_BOARD_LABELS[m.b]}
                </span>
              </Fragment>
            ))}
          </span>
        )}
        {hasTax && (
          <span className="pms-ge-chip" title="Taxe de séjour : nuitées taxables (adultes) et montant — collectée pour la commune, hors chiffre d'affaires">
            TS {taxNights} nuitée{taxNights > 1 ? 's' : ''} / {fmtEuros(taxCents)}
          </span>
        )}
        {/* The money, apart and to the right of the facts about the stay. */}
        {form.id !== null && advance !== undefined && (
          <PaymentChips advance={advance} totalCents={roomNights > 0 ? total : 0} />
        )}
      </div>

      {/* Vega's two records of the group disagree — its charges against its room
          allocation. A flag, never a correction: to be checked in Vega (planGapView). */}
      {form.id !== null && planGap !== undefined && planGap !== null && (
        typeof planGap === 'string' ? (
          <p className="pms-ge-alert is-muted">Contrôle de la facturation Vega non lu : {planGap}</p>
        ) : (
          <p className="pms-ge-alert" role="status" title={planGapTitle(planGap)}>
            {planGapText(planGap)} — {planGapNote(planGap)}
          </p>
        )
      )}

      {/* The client's own fields — open for a new booking, folded once known. */}
      <details className="pms-ge-details" open={form.guestId === ''}>
        <summary>Client, canal et notes</summary>
        <div className="pms-form-grid">
          <label className="pms-field">
            <span>Nom *</span>
            <input value={form.lastName} disabled={identityLocked} onChange={(e) => edit(set)({ lastName: e.target.value })} />
          </label>
          <label className="pms-field">
            {/* Required for a NEW client only: 102 cardex clients have none. */}
            <span>Prénom{form.guestId ? '' : ' *'}</span>
            <input value={form.firstName} disabled={identityLocked} onChange={(e) => edit(set)({ firstName: e.target.value })} />
          </label>
          <label className="pms-field">
            <span>Email</span>
            <input type="email" value={form.email} disabled={identityLocked} onChange={(e) => edit(set)({ email: e.target.value })} />
          </label>
          <label className="pms-field">
            <span>Téléphone</span>
            <input value={form.phone} disabled={identityLocked} onChange={(e) => edit(set)({ phone: e.target.value })} />
          </label>
          <label className="pms-field">
            <span>Langue</span>
            <select value={form.lang} onChange={(e) => edit(set)({ lang: e.target.value })}>
              <option value="fr">FR</option>
              <option value="en">EN</option>
              <option value="es">ES</option>
              <option value="de">DE</option>
            </select>
          </label>
          {form.id === null && (
            <label className="pms-field">
              <span>Statut</span>
              <select value={form.status} onChange={(e) => edit(set)({ status: e.target.value as 'reserved' | 'confirmed' })}>
                {/* Labels from the status map, never typed here. */}
                <option value="confirmed">{PMS_STATUS_LABELS_FR.confirmed}</option>
                <option value="reserved">{PMS_STATUS_LABELS_FR.reserved}</option>
              </select>
            </label>
          )}
          <label className="pms-field">
            <span>Canal (miroir OTA)</span>
            <select value={form.channel} onChange={(e) => edit(set)({ channel: e.target.value })}>
              <option value="">— Direct / téléphone —</option>
              <option value="booking.com">Booking.com</option>
              <option value="expedia">Expedia</option>
              <option value="airbnb">Airbnb</option>
              <option value="siteminder-direct">SiteMinder direct-book</option>
            </select>
          </label>
          <label className="pms-field">
            <span>N° confirmation OTA</span>
            <input value={form.channelRef} onChange={(e) => edit(set)({ channelRef: e.target.value })} />
          </label>
          <label className="pms-field pms-field-wide">
            <span>Notes</span>
            <input value={form.notes} onChange={(e) => edit(set)({ notes: e.target.value })} />
          </label>
          {identityLocked && held && (
            <span className="pms-hint pms-field-wide">
              Nom et coordonnées tenus par Vega jusqu’à la mise en service — à corriger dans Vega.
            </span>
          )}
        </div>
      </details>

      {/* ── The action rows ─────────────────────────────────────────────── */}
      <div className="pms-ge-actions" role="toolbar" aria-label="Pour tous">
        <span className="pms-ge-actions-label">Pour tous</span>
        {PMS_MEALS.map((meal) => {
          const on = allHave(meal);
          const locked = allInclude(meal);
          return (
            <button
              key={meal}
              type="button"
              role="switch"
              aria-checked={on}
              className={on ? 'pms-ge-switch is-on' : 'pms-ge-switch'}
              disabled={locked}
              title={
                locked
                  ? `${PMS_MEAL_LABELS_FR[meal]} inclus dans le type de séjour de tous les logements`
                  : `${PMS_MEAL_LABELS_FR[meal]} pour toutes les personnes présentes, chaque jour — les exceptions individuelles restent`
              }
              onClick={edit(() => setForm((f) => setRoomsMeal(f, targets('all'), meal, !on)))}
            >
              {PMS_MEAL_LABELS_FR[meal]}
            </button>
          );
        })}
        <PanelButtons scope="all" panel={panel} setPanel={setPanel} canProduct={extras.length > 0} />
      </div>
      {multi && (
        <div className="pms-ge-actions" role="toolbar" aria-label="Sélection">
          <span className="pms-ge-actions-label">Sélection ({selected.size})</span>
          {PMS_MEALS.map((meal) => (
            <span key={meal} className="pms-ge-pair">
              <button
                type="button"
                className="pms-mini"
                disabled={selected.size === 0}
                onClick={edit(() => setForm((f) => setRoomsMeal(f, targets('selection'), meal, true)))}
              >
                + {PMS_MEAL_LABELS_FR[meal]}
              </button>
              <button
                type="button"
                className="pms-mini"
                disabled={selected.size === 0}
                onClick={edit(() => setForm((f) => setRoomsMeal(f, targets('selection'), meal, false)))}
              >
                − {PMS_MEAL_LABELS_FR[meal]}
              </button>
            </span>
          ))}
          <PanelButtons
            scope="selection"
            panel={panel}
            setPanel={setPanel}
            disabled={selected.size === 0}
            canProduct={extras.length > 0}
          />
        </div>
      )}

      {panel !== null && typeof panel.scope !== 'number' && (
        <BulkPanel
          panel={panel}
          span={span}
          extras={extras}
          count={targets(panel.scope).length}
          onClose={() => setPanel(null)}
          onDates={edit((a: string, d: string) => setForm((f) => setRoomsDates(f, targets(panel.scope), a, d)))}
          onPrice={edit((euros: string) => setForm((f) => setRoomsPrice(f, targets(panel.scope), euros)))}
          onProduct={edit((id: number) => setForm((f) => addRoomsProduct(f, targets(panel.scope), id)))}
        />
      )}

      {/* ── The rooming list: one row per logement, every logement (Q11) ─── */}
      <div className="pms-ge-scroll">
        <table className="pms-ge-table">
          <thead>
            <tr>
              {multi && (
                <th className="pms-ge-check">
                  <input
                    type="checkbox"
                    aria-label="Tout sélectionner"
                    checked={selected.size === form.rooms.length}
                    onChange={(e) => setSelected(e.target.checked ? new Set(form.rooms.map((_, i) => i)) : new Set())}
                  />
                </th>
              )}
              <th>Logement</th>
              <th>Pers.</th>
              <th title="Nombre de nuits du logement (la taxe de séjour est dans le bandeau du haut)">Nuits</th>
              <th>€/nuit</th>
              <th title="Catégorie vendue : celle du tarif, pas celle du logement attribué">Cat. vendue</th>
              <th>Type</th>
              {PMS_MEALS.map((m) => (
                <th key={m} className="pms-ge-meal-h">
                  {PMS_MEAL_LABELS_FR[m]}
                </th>
              ))}
              <th>Autres</th>
              <th className="pms-ge-num">Total</th>
            </tr>
          </thead>
          <tbody>
            {form.rooms.map((room, i) => {
              const board = boards[i]!;
              const pv = previews[i]!;
              const unit = room.roomId === '' ? null : (rooms.find((r) => String(r.id) === room.roomId) ?? null);
              // Two categories, never one standing in for the other: beside the room, the ALLOCATED
              // room's own (or, unallocated, the one to allocate in); in « Cat. vendue », the one the
              // rate was SOLD as — an upgrade given in Vega is still a stay sold as, say, DSUP, and the
              // way back down is to that category.
              const placedId = unit !== null && unit.roomTypeId !== null ? String(unit.roomTypeId) : room.roomTypeId;
              const placedType = roomTypes.find((t) => String(t.id) === placedId);
              // A Vega stay's sold category is Vega's or nothing: its roomTypeId is the room it was put in.
              const soldId = room.soldRoomTypeId !== '' ? room.soldRoomTypeId : room.fromVega === true ? '' : room.roomTypeId;
              const soldType = roomTypes.find((t) => String(t.id) === soldId);
              const nights = pmsNightVector(room.checkin, room.checkout, room.nights, parseEurosToCents(room.priceEuros) ?? 0);
              const prices = [...new Set(nights.map((n) => n.priceCents))];
              const counts = roomCounts(room);
              const mark = roomNightsMark(nights, counts.adults + counts.children);
              const firstName = room.persons.find((p) => p.name.trim() !== '')?.name.trim() ?? room.occupantName.trim();
              const isOpen = open.has(i);
              const cols = 11 + (multi ? 1 : 0);
              return (
                <Fragment key={i /* rooms are never reordered; a new one has no id yet */}>
                  <tr
                    className={isOpen ? 'pms-ge-row is-open' : 'pms-ge-row'}
                    onClick={() => toggleOpen(i)}
                    aria-expanded={isOpen}
                  >
                    {multi && (
                      <td className="pms-ge-check" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Sélectionner le logement ${i + 1}`}
                          checked={selected.has(i)}
                          onChange={() => toggleSelected(i)}
                        />
                      </td>
                    )}
                    <td className="pms-ge-unit">
                      <strong>{unit?.roomNumber ?? '—'}</strong> <span
                        className="pms-ge-cat"
                        title={placedType === undefined ? undefined : `Logement de catégorie ${placedType.code}`}
                      >
                        {placedType?.code ?? ''}
                      </span>
                      <div className="pms-ge-sub">
                        {firstName !== '' ? firstName : multi ? '— groupe —' : '— le client —'}
                      </div>
                      {(room.checkin !== span.checkin || room.checkout !== span.checkout) && (
                        <div className="pms-ge-sub is-dates">
                          {dm(room.checkin)} → {dm(room.checkout)}
                        </div>
                      )}
                    </td>
                    <td>{pmsPersonsShort(room.persons)}</td>
                    <td className="pms-ge-num" title={`${nights.length} nuit${nights.length > 1 ? 's' : ''}`}>
                      {nights.length}
                    </td>
                    <td
                      className="pms-ge-num"
                      title={[mark.title, mark.varies ? 'Prix ou quantités différents selon les nuits' : null].filter((x) => x !== null).join('\n') || undefined}
                    >
                      {prices.length === 0 ? '—' : `${fmtAmount(prices[0]!)}${mark.suffix}`}
                      {mark.varies && <span className="pms-ge-mark">*</span>}
                    </td>
                    <td
                      className="pms-ge-sold"
                      title={
                        soldType === undefined
                          ? room.fromVega === true
                            ? room.nights.some((n) => n.priceCents > 0)
                              ? 'Catégorie vendue inconnue : le tarif Vega de ce logement ne nomme pas de catégorie'
                              : 'Catégorie vendue inconnue : la facturation Vega du groupe ne couvre pas ce logement'
                            : undefined
                          : placedType !== undefined && soldType.id !== placedType.id
                            ? `Vendu en ${soldType.code} — logement de catégorie ${placedType.code}`
                            : `Vendu en ${soldType.code}`
                      }
                    >
                      {soldType?.code ?? '—'}
                    </td>
                    <td>
                      <span className={`pms-board is-${board}`} title={PMS_BOARD_TITLES_FR[board]}>
                        {PMS_BOARD_LABELS[board]}
                      </span>
                    </td>
                    {PMS_MEALS.map((meal) => (
                      <td key={meal} className="pms-ge-meal" onClick={(e) => e.stopPropagation()}>
                        <MealCellButton
                          meal={meal}
                          board={board}
                          cell={pv.meals[meal]}
                          switchedOn={(room.meals ?? []).includes(meal)}
                          onToggle={edit(() => setForm((f) => setRoomsMeal(f, [i], meal, !(f.rooms[i]!.meals ?? []).includes(meal))))}
                        />
                      </td>
                    ))}
                    <td className="pms-ge-extras" onClick={(e) => e.stopPropagation()}>
                      {room.products.map((r) => {
                        const p = products.find((x) => x.id === r.productId);
                        return (
                          <span key={r.productId} className="pms-ge-extra">
                            {p?.labelFr ?? `#${r.productId}`}
                            {r.quantity > 1 || p?.manualQty ? ` ×${r.quantity}` : ''}
                            <button
                              type="button"
                              aria-label={`Retirer ${p?.labelFr ?? 'la prestation'}`}
                              onClick={edit(() => mapRoom(i, (x) => setRoomProductQty(x, r.productId, 0)))}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                      {extras.length > 0 && (
                        <button
                          type="button"
                          className="pms-mini"
                          aria-label="Ajouter une prestation"
                          onClick={() => setPanel({ scope: i, kind: 'product' })}
                        >
                          +
                        </button>
                      )}
                    </td>
                    <td className="pms-ge-num">{fmtAmount(pv.totalCents)}</td>
                  </tr>
                  {panel !== null && panel.scope === i && (
                    <tr className="pms-ge-panel-row">
                      <td colSpan={cols}>
                        <ProductPicker
                          extras={extras}
                          onPick={edit((id: number) => {
                            setForm((f) => addRoomsProduct(f, [i], id));
                            setPanel(null);
                          })}
                          onClose={() => setPanel(null)}
                        />
                      </td>
                    </tr>
                  )}
                  {isOpen && (
                    <tr className="pms-ge-detail-row">
                      <td colSpan={cols}>
                        <RoomDetail
                          room={room}
                          index={i}
                          multi={multi}
                          board={board}
                          roomTypes={roomTypes}
                          unit={unit}
                          automatic={automatic}
                          canSeeNeeds={canSeeNeeds}
                          onRoom={edit((patch: Partial<RoomDraft>) => setRoom(i, patch))}
                          onMap={edit((fn: (r: RoomDraft) => RoomDraft) => mapRoom(i, fn))}
                          onRemove={() => removeRoomAt(i)}
                        />
                        <SoldTermsNote
                          terms={room.cancellationTerms}
                          saved={room.id !== null}
                          arrival={room.checkin}
                          totalCents={pv.totalCents - pv.taxCents}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="pms-room-add">
        <button
          type="button"
          className="auth-btn is-ghost"
          onClick={edit(() => {
            setForm(addRoom);
            setOpen((s) => new Set([...s, form.rooms.length]));
          })}
        >
          + Ajouter un logement
        </button>
      </div>

      </fieldset>

      {billed.length > 0 && <BilledList lines={billed} />}

      {journal.length > 0 && <Journal lines={journal} />}

      {/* Repeated beside the button: the page banner may be scrolled away. */}
      {error && <p className="auth-err">Erreur : {error}</p>}

      <div className="pms-form-actions">
        <span className="pms-total">
          {roomNights > 0
            ? `${roomNights} nuit${roomNights > 1 ? 's' : ''}${multi ? ` sur ${form.rooms.length} logements` : ''} · Total ${fmtEuros(total)}`
            : ''}
        </span>
        {vegaHeld ? (
          <button type="button" className="auth-btn is-ghost" onClick={onCancel}>
            Fermer
          </button>
        ) : (
          <>
            <button type="button" className="auth-btn is-ghost" onClick={onCancel} disabled={saving}>
              Annuler
            </button>
            <button
              type="button"
              className="auth-btn"
              onClick={() => onSave(form, etab)}
              disabled={saving || !formIsComplete(form) || (several && etab === null)}
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </>
        )}
      </div>
    </section>
  );
}

// ── A meal cell: a number, coloured by what it costs (§2.6) ─────────────────

function MealCellButton({
  meal,
  board,
  cell,
  switchedOn,
  onToggle,
}: {
  meal: MealPrestation;
  board: PmsBoard;
  cell: RoomPreview['meals'][MealPrestation];
  switchedOn: boolean;
  onToggle: () => void;
}) {
  const label = PMS_MEAL_LABELS_FR[meal];
  if (cell.included) {
    return (
      <span className={`pms-ge-cell is-included is-${board}`} title={`${label} inclus dans le type de séjour — effectif seulement`}>
        {cell.taken > 0 ? cell.taken : '—'}
      </span>
    );
  }
  const partial = cell.taken > 0 && cell.taken < cell.slots;
  const carte = meal === 'dinner';
  const cls = cell.taken === 0 ? 'pms-ge-cell' : partial ? 'pms-ge-cell is-partial' : 'pms-ge-cell is-billed';
  const what = carte ? 'couverts prévus à la carte, facturés au restaurant' : 'facturé';
  const title =
    cell.taken === 0
      ? `${label} : aucun — cliquer pour l'ajouter à toutes les personnes présentes`
      : `${label} : ${cell.taken} ${what}${partial ? ` (${cell.slots - cell.taken} absence${cell.slots - cell.taken > 1 ? 's' : ''})` : ''} — cliquer pour ${switchedOn ? 'retirer' : 'ajouter'} pour la chambre`;
  return (
    <button type="button" className={cls} aria-pressed={switchedOn} title={title} onClick={onToggle}>
      {cell.taken > 0 ? cell.taken : '—'}
      {cell.unbilledUnknownBand > 0 && (
        <span className="pms-ge-warn" title="Âge d'un enfant à préciser : son petit-déjeuner n'est pas facturé">
          !
        </span>
      )}
    </button>
  );
}

// ── Dates… · Prix… · + Prestation ───────────────────────────────────────────

function PanelButtons({
  scope,
  panel,
  setPanel,
  disabled = false,
  canProduct,
}: {
  scope: 'all' | 'selection';
  panel: Panel;
  setPanel: (p: Panel) => void;
  disabled?: boolean;
  canProduct: boolean;
}) {
  const toggle = (kind: 'dates' | 'price' | 'product') =>
    setPanel(panel !== null && panel.scope === scope && panel.kind === kind ? null : { scope, kind });
  return (
    <>
      <button type="button" className="pms-mini" disabled={disabled} onClick={() => toggle('dates')}>
        Dates…
      </button>
      <button type="button" className="pms-mini" disabled={disabled} onClick={() => toggle('price')}>
        Prix…
      </button>
      {canProduct && (
        <button type="button" className="pms-mini" disabled={disabled} onClick={() => toggle('product')}>
          + Prestation
        </button>
      )}
    </>
  );
}

function BulkPanel({
  panel,
  span,
  extras,
  count,
  onClose,
  onDates,
  onPrice,
  onProduct,
}: {
  panel: NonNullable<Panel>;
  span: { checkin: string; checkout: string };
  extras: Product[];
  count: number;
  onClose: () => void;
  onDates: (checkin: string, checkout: string) => void;
  onPrice: (euros: string) => void;
  onProduct: (id: number) => void;
}) {
  const [a, setA] = useState(span.checkin);
  const [d, setD] = useState(span.checkout);
  const [euros, setEuros] = useState('');
  const who = panel.scope === 'all' ? 'tous les logements' : `${count} logement${count > 1 ? 's' : ''}`;
  if (panel.kind === 'product') {
    return (
      <div className="pms-ge-panel">
        <span className="pms-hint">Ajouter à {who} :</span>
        <ProductPicker
          extras={extras}
          onPick={(id) => {
            onProduct(id);
            onClose();
          }}
          onClose={onClose}
        />
      </div>
    );
  }
  return (
    <div className="pms-ge-panel">
      {panel.kind === 'dates' ? (
        <>
          <label className="pms-field">
            <span>Arrivée</span>
            <input type="date" value={a} onChange={(e) => setA(e.target.value)} />
          </label>
          <label className="pms-field">
            <span>Départ</span>
            <input type="date" value={d} min={a} onChange={(e) => setD(e.target.value)} />
          </label>
          <button
            type="button"
            className="auth-btn"
            disabled={pmsNightCount(a, d) <= 0}
            onClick={() => {
              onDates(a, d);
              onClose();
            }}
          >
            Appliquer à {who}
          </button>
          <span className="pms-hint">Les personnes suivent les dates de leur logement ; un logement arrivé garde son arrivée.</span>
        </>
      ) : (
        <>
          <label className="pms-field">
            <span>Prix / nuit (€)</span>
            <input inputMode="decimal" placeholder="95,00" value={euros} onChange={(e) => setEuros(e.target.value)} />
          </label>
          <button
            type="button"
            className="auth-btn"
            disabled={parseEurosToCents(euros) === null}
            onClick={() => {
              onPrice(euros);
              onClose();
            }}
          >
            Appliquer à {who}
          </button>
        </>
      )}
      <button type="button" className="auth-btn is-ghost" onClick={onClose}>
        Fermer
      </button>
    </div>
  );
}

/** A searchable list of agreed extras. */
function ProductPicker({ extras, onPick, onClose }: { extras: Product[]; onPick: (id: number) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const hits = useMemo(() => {
    const k = q.trim().toLowerCase();
    return k === '' ? extras : extras.filter((p) => p.labelFr.toLowerCase().includes(k) || p.code.toLowerCase().includes(k));
  }, [q, extras]);
  return (
    <div className="pms-ge-picker">
      <input autoFocus placeholder="Rechercher une prestation…" value={q} onChange={(e) => setQ(e.target.value)} />
      <ul>
        {hits.map((p) => (
          <li key={p.id}>
            <button type="button" onClick={() => onPick(p.id)}>
              {p.emoji ? `${p.emoji} ` : ''}
              {p.labelFr} — {fmtEuros(p.priceCents)}
              {p.perPerson ? ' / pers.' : ''}
              {p.appliesTo === 'each_night' ? ' / nuit' : p.appliesTo === 'each_morning' ? ' / matin' : ''}
            </button>
          </li>
        ))}
        {hits.length === 0 && <li className="pms-hint">Aucune prestation ne correspond.</li>}
      </ul>
      <button type="button" className="auth-btn is-ghost" onClick={onClose}>
        Fermer
      </button>
    </div>
  );
}

// ── A person's needs and allergens (group edit step 5) — health data ───────

interface PersonNeedsRowProps {
  index: number;
  health: PersonHealth | null;
  editable: boolean;
  colSpan: number;
  onNeeds: (text: string) => void;
  onAllergen: (a: Allergen) => void;
}

function PersonNeedsRow({ index, health, editable, colSpan, onNeeds, onAllergen }: PersonNeedsRowProps) {
  const ticked = health?.allergens ?? [];
  // In the regulation's order, whatever order they were ticked in.
  const summary = PMS_ALLERGENS.filter((a) => ticked.includes(a)).map((a) => ALLERGEN_LABELS_FR[a]);
  return (
    <tr className="pms-ge-needs-row">
      <td />
      <td colSpan={colSpan}>
        <div className="pms-ge-needs">
          <input
            className="pms-ge-needs-text"
            value={health?.needs ?? ''}
            placeholder="Besoins : allergies, régime, mobilité…"
            disabled={!editable}
            aria-label={`Besoins de la personne ${index + 1}`}
            onChange={(e) => onNeeds(e.target.value)}
          />
          <details className="pms-ge-allergens">
            <summary className={summary.length > 0 ? 'has-allergens' : undefined}>
              Allergènes : {summary.length > 0 ? summary.join(', ') : 'aucun'}
            </summary>
            <div role="group" aria-label={`Allergènes de la personne ${index + 1}`}>
              {PMS_ALLERGENS.map((a) => (
                <button
                  key={a}
                  type="button"
                  className={`pms-ge-allergen${ticked.includes(a) ? ' is-on' : ''}`}
                  aria-pressed={ticked.includes(a)}
                  disabled={!editable}
                  onClick={() => onAllergen(a)}
                >
                  {ALLERGEN_LABELS_FR[a]}
                </button>
              ))}
            </div>
          </details>
        </div>
      </td>
    </tr>
  );
}

// ── The detail of one logement: its persons, their meals, its nights ────────

interface RoomDetailProps {
  room: RoomDraft;
  index: number;
  multi: boolean;
  board: PmsBoard;
  roomTypes: RoomType[];
  unit: Room | null;
  automatic: Product[];
  canSeeNeeds: boolean;
  onRoom: (patch: Partial<RoomDraft>) => void;
  onMap: (fn: (r: RoomDraft) => RoomDraft) => void;
  onRemove: () => void;
}

function RoomDetail({ room, index, multi, board, roomTypes, unit, automatic, canSeeNeeds, onRoom, onMap, onRemove }: RoomDetailProps) {
  // PER LOGEMENT (0091): a sibling being in-house must not freeze this one.
  const rule = pmsEditRule(room.origStatus ?? 'reserved');
  const priceCents = parseEurosToCents(room.priceEuros);
  // DERIVED on every render: `buildReservationWrite` makes the identical call,
  // so what is shown is exactly what is saved.
  const nights = pmsNightVector(room.checkin, room.checkout, room.nights, priceCents ?? 0);
  const days = stayNights(room.checkin, room.checkout);
  const editable = room.personsLoaded;

  const setNight = (date: string, euros: string) => {
    const cents = parseEurosToCents(euros);
    if (cents === null) return; // mid-typing (« 95, ») — keep the last good value
    onRoom({ nights: nights.map((n) => (n.date === date ? { ...n, priceCents: cents } : n)) });
  };

  return (
    <div className="pms-ge-detail">
      {room.id === null && unit !== null && !multi && (
        <p className="pms-hint">
          Logement <strong>{unit.roomNumber}</strong> — attribué à l'enregistrement.
        </p>
      )}

      <div className="pms-form-grid">
        <label className="pms-field">
          <span>Catégorie *</span>
          <select
            value={room.roomTypeId}
            // The unit fixes the category: a mismatch is refused by
            // pms_assign_room_stay — better not offered than offered and rejected.
            disabled={unit !== null}
            onChange={(e) => onRoom({ roomTypeId: e.target.value })}
          >
            <option value="">—</option>
            {roomTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.code} — {t.labelFr}
              </option>
            ))}
          </select>
        </label>
        <label className="pms-field">
          <span>Arrivée *</span>
          <input type="date" value={room.checkin} disabled={!rule.checkin} onChange={(e) => onRoom({ checkin: e.target.value })} />
        </label>
        <label className="pms-field">
          <span>Départ *</span>
          <input
            type="date"
            value={room.checkout}
            min={rule.checkout === 'extend_only' ? room.origCheckout : undefined}
            onChange={(e) => onRoom({ checkout: e.target.value })}
          />
        </label>
        <label className="pms-field">
          <span>Prix / nuit (€) *</span>
          <input inputMode="decimal" placeholder="95,00" value={room.priceEuros} onChange={(e) => onRoom({ priceEuros: e.target.value })} />
        </label>
        {multi && (
          <label className="pms-field">
            <span>Occupant</span>
            <input value={room.occupantName} placeholder="— le client —" onChange={(e) => onRoom({ occupantName: e.target.value })} />
          </label>
        )}
        <div className="pms-field">
          <span>Type de séjour</span>
          <span className={`pms-board is-${board}`}>{PMS_BOARD_TITLES_FR[board]}</span>
        </div>
      </div>

      {/* ── Who is in the room ─────────────────────────────────────────── */}
      <div className="pms-ge-persons">
        <div className="pms-nights-head">
          <strong>Personnes</strong>
          {!editable && <span className="pms-hint">Chargement des personnes…</span>}
        </div>
        <table className="pms-ge-ptable">
          <thead>
            <tr>
              <th>#</th>
              <th>Nom</th>
              <th>Âge</th>
              <th>Arrivée</th>
              <th>Départ</th>
              {PMS_MEALS.map((m) => (
                <th key={m} className="pms-ge-days-h">
                  <span>{PMS_MEAL_LABELS_FR[m]}</span>
                  <span className="pms-ge-days">
                    {days.map((d) => (
                      <span key={d} className="pms-ge-dayno">
                        {d.slice(8, 10)}
                      </span>
                    ))}
                  </span>
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {room.persons.map((p, k) => (
              <Fragment key={p.key}>
                <tr>
                  <td className="pms-ge-pos">{k + 1}</td>
                  <td>
                    <input
                      className="pms-ge-pname"
                      value={p.name}
                      placeholder={personLabel(room.persons, k)}
                      disabled={!editable}
                      aria-label={`Nom de la personne ${k + 1}`}
                      onChange={(e) => onMap((r) => patchPerson(r, p.key, { name: e.target.value }))}
                    />
                  </td>
                  <td>
                    <select
                      value={p.ageBand}
                      disabled={!editable}
                      aria-label={`Âge de la personne ${k + 1}`}
                      onChange={(e) => onMap((r) => patchPerson(r, p.key, { ageBand: e.target.value as AgeBand }))}
                    >
                      {PMS_AGE_BANDS.filter((b) => b !== 'child_unknown' || p.ageBand === 'child_unknown').map((b) => (
                        <option key={b} value={b}>
                          {PMS_AGE_BAND_LABELS_FR[b]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="date"
                      value={p.startDate}
                      min={room.checkin}
                      max={p.endDate}
                      disabled={!editable}
                      aria-label={`Arrivée de la personne ${k + 1}`}
                      onChange={(e) => e.target.value && onMap((r) => patchPerson(r, p.key, { startDate: e.target.value }))}
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      value={p.endDate}
                      min={p.startDate}
                      max={room.checkout}
                      disabled={!editable}
                      aria-label={`Départ de la personne ${k + 1}`}
                      onChange={(e) => e.target.value && onMap((r) => patchPerson(r, p.key, { endDate: e.target.value }))}
                    />
                  </td>
                  {PMS_MEALS.map((meal) => (
                    <td key={meal} className="pms-ge-days">
                      {p.ageBand === 'infant' ? (
                        <span className="pms-hint" title="Bébé : compté pour rien (D1)">
                          —
                        </span>
                      ) : (
                        days.map((night) => {
                          const present = night >= p.startDate && night < p.endDate;
                          if (!present) return <span key={night} className="pms-ge-day is-absent" />;
                          const date = pmsMealServiceDate(night, meal);
                          const taken = pmsMealTaken(board, room.meals ?? [], p.exceptions, date, meal);
                          const included = pmsMealIncluded(board, meal);
                          const cls = `pms-ge-day${taken ? (included ? ` is-included is-${board}` : ' is-billed') : ''}`;
                          return (
                            <button
                              key={night}
                              type="button"
                              className={cls}
                              aria-pressed={taken}
                              disabled={!editable}
                              title={`${PMS_MEAL_LABELS_FR[meal]} du ${fmtDate(date)} — ${taken ? (included ? 'inclus' : meal === 'dinner' ? 'à la carte' : 'facturé') : 'non pris'}`}
                              onClick={() => onMap((r) => togglePersonMeal(r, board, p.key, date, meal))}
                            />
                          );
                        })
                      )}
                    </td>
                  ))}
                  <td>
                    {room.persons.length > 1 && (
                      <button
                        type="button"
                        className="pms-link"
                        disabled={!editable}
                        aria-label={`Retirer la personne ${k + 1}`}
                        onClick={() => onMap((r) => removePerson(r, p.key))}
                      >
                        Retirer
                      </button>
                    )}
                  </td>
                </tr>
                {canSeeNeeds && (
                  <PersonNeedsRow
                    index={k}
                    health={p.health}
                    // A stored person whose needs were not read stays locked: a
                    // value typed over an unread one would replace it blind.
                    editable={editable && (p.health !== null || p.id === null)}
                    colSpan={5 + PMS_MEALS.length}
                    onNeeds={(text) => onMap((r) => setPersonNeeds(r, p.key, text))}
                    onAllergen={(a) => onMap((r) => togglePersonAllergen(r, p.key, a))}
                  />
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        {canSeeNeeds && (
          <p className="pms-hint pms-ge-needs-source">
            Besoins et allergies : donnés par le client, ou par l'organisateur pour un groupe. Données de santé — visibles
            de la réception et de la cuisine seulement, effacées 30 jours après le départ.
          </p>
        )}
        <button type="button" className="pms-mini" disabled={!editable} onClick={() => onMap((r) => addPerson(r))}>
          + Personne
        </button>
        {automatic.map((p) => (
          <span className="pms-hint" key={p.id}>
            {' '}
            · {p.emoji ? `${p.emoji} ` : ''}
            {p.labelFr} — appliqué automatiquement
          </span>
        ))}
      </div>

      {/* ── The per-night prices ───────────────────────────────────────── */}
      {nights.length > 0 && (
        <div className="pms-nights">
          <div className="pms-nights-head">
            <strong>Détail par nuit</strong>
            <button
              type="button"
              className="auth-btn is-ghost"
              onClick={() => priceCents !== null && onRoom({ nights: nights.map((n) => ({ ...n, priceCents })) })}
              disabled={priceCents === null}
              title="Reporter le prix ci-dessus sur toutes les nuits"
            >
              Appliquer à toutes les nuits
            </button>
          </div>
          <div className="pms-nights-grid">
            {nights.map((n) => (
              <label className="pms-field" key={`${n.date}:${n.priceCents}`}>
                <span>{fmtDate(n.date)}</span>
                {/* Uncontrolled, re-keyed on the value: a controlled input
                    reformatted « 95, » on every keystroke. */}
                <input
                  inputMode="decimal"
                  defaultValue={String(n.priceCents / 100).replace('.', ',')}
                  onChange={(e) => setNight(n.date, e.target.value)}
                />
              </label>
            ))}
          </div>
        </div>
      )}

      {!rule.checkin && (
        <p className="pms-hint">
          Arrivée effectuée le {fmtDate(room.origCheckin)} : la date d'arrivée n'est plus modifiable et le départ ne peut
          être que prolongé. Pour corriger l'arrivée, utilisez « Annuler l'arrivée » (un motif est demandé), puis modifiez.
        </p>
      )}

      {multi && (
        <div className="pms-ge-detail-foot">
          <button type="button" className="auth-btn is-ghost" onClick={onRemove}>
            Retirer le logement {index + 1}
          </button>
        </div>
      )}
    </div>
  );
}
